"use client";

import { useEffect } from "react";

/**
 * Preloads /images/grain.webp and toggles `body.grain-ready` once the image is fully
 * decoded. The grain texture lives on `body::before` with opacity 0, so the
 * user never sees the top-to-bottom band paint that happens while the PNG
 * downloads progressively. Once the class is on, CSS fades the overlay in.
 *
 * On repeat visits the image is served from the immutable long-cache
 * (`Cache-Control: public, max-age=31536000, immutable` — see next.config.ts),
 * so `img.complete` is true synchronously and the grain appears immediately.
 */
const GRAIN_URL = "/images/grain.webp";

const GrainLoader = () => {
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.body.classList.contains("grain-ready")) return;

    const markReady = () => {
      document.body.classList.add("grain-ready");
    };

    const img = new Image();
    img.decoding = "async";
    img.src = GRAIN_URL;

    if (img.complete && img.naturalWidth > 0) {
      markReady();
      return;
    }

    img.addEventListener("load", markReady, { once: true });
    // If the image errors for any reason, still reveal so we never leave the
    // site in a "no texture, forever waiting" state.
    img.addEventListener("error", markReady, { once: true });

    return () => {
      img.removeEventListener("load", markReady);
      img.removeEventListener("error", markReady);
    };
  }, []);

  return null;
};

export default GrainLoader;
